import { getNormalizedApiData } from '../../ApiHelpers';
import { getAuthClient } from '../../auth';
import { personalizationNormalizeDrupalData } from '../../../Components/Personalization/PersonalizationNormalizedData';

const auth = getAuthClient();

/**
 * Creates API request object for resident data
 * @param {string} residentUuid - The resident UUID
 * @param {Array} includes - Array of include fields
 * @returns {Object} API request object
 */
export const createResidentApiRequest = ( residentUuid, includes ) => ( {
  entityType: 'node',
  contentType: 'resident',
  uuid: residentUuid,
  params: {
    sort: 'title',
    include: includes.join( ',' ),
  },
} );

/**
 * Fetches data from Drupal for Residents that match any of the params.
 *
 * @param {string} email
 *   User email.
 * @param {string} firstName
 *   User first name.
 * @param {string} lastName
 *   User last name.
 * @param {array} crmIds
 *   User(s) crm IDs.
 * @param {string} communityNid
 *  Community Drupal node id.
 *
 * @returns {Array}
 *   List of users that match search criteria.
 */
export async function fetchResidentsFromDrupal(
  email,
  firstName,
  lastName,
  crmId,
  communityNid,
) {
  // Build filters based on filled out fields from the form.
  const filter = {};
  if ( email && email !== '' ) {
    filter.field_email = email;
  }
  if ( firstName && firstName !== '' ) {
    filter.field_first_name = firstName;
  }
  if ( lastName && lastName !== '' ) {
    filter.field_last_name = lastName;
  }
  if ( crmId && crmId !== '' ) {
    filter.field_crm_individual_id = crmId;
  }

  const includes = [
    'field_spouse',
    'field_favorite_floor_plans',
    'field_favorite_amenities',
    'field_favorite_media',
    'field_favorite_media.field_gallery_photos',
    'field_favorite_media.field_three_sixties',
    'field_favorite_residences',
  ];

  const request = {
    entityType: 'node',
    contentType: 'resident',
    params: {
      sort: 'title',
      include: includes.join( ',' ),
    },
    filter,
  };

  // Fetch data
  const residents = getNormalizedApiData(
    request,
    communityNid,
    personalizationNormalizeDrupalData,
  );

  return residents;
}

/**
 * Finds the Resident already representing this person, if there is one.
 *
 * The CRM's own individual id is the authoritative key and is tried first: it
 * is unique per person, so a match on it is certain.
 *
 * It is not always there, though. Sherpa, Lansdale and Enquire defer their
 * create until a couple is complete, so a solo Resident created from a chatbot
 * conversion carried no `field_crm_individual_id` at all — the id is what the
 * CRM hands back on create, and the create had not happened. Drupal now
 * completes it at conversion time, but every lead converted before that fix
 * still has an empty field, and matching on the id alone made those people look
 * new. Personalization then posted a second Resident node for them, orphaning
 * the first one's chat history, favourites and brochure activity.
 *
 * So when the id finds nothing, fall back to the identity the two sides do
 * share: email together with first and last name, in this community. All three
 * are ANDed, which is deliberately the same rule Drupal applies on the
 * conversion side (ResidentConversionService::findExistingResident). Email
 * alone would be wrong — couples share one address, and a husband and wife
 * touring together are two Resident records — and a name alone cannot tell two
 * people of the same name apart. Anything less than all three is not an
 * identity, so an incomplete set matches nobody rather than guessing.
 *
 * @param {Object} person
 *   The person selected from the CRM: crmIndividualId, email, firstName,
 *   lastName.
 * @param {string} communityNid
 *   Community Drupal node id, which scopes every match — the same person
 *   prospecting at two communities is two Resident records by design.
 *
 * @returns {Object|null}
 *   The matching Resident, or null if this person is not one yet.
 */
export async function findExistingResidentInDrupal( person, communityNid ) {
  const crmId = person?.crmIndividualId ?? '';
  const email = person?.email ?? '';
  const firstName = person?.firstName ?? '';
  const lastName = person?.lastName ?? '';

  if ( crmId ) {
    const byCrmId = await fetchResidentsFromDrupal(
      null,
      null,
      null,
      crmId,
      communityNid,
    );

    // Take the first match. Accepted behavior, not a shortcut: the filter is on
    // field_crm_individual_id, which the CRM guarantees is unique per person, so
    // more than one match means the CMS has duplicates of the same person.
    // Either one carries the same CRM ids, so taking the first is stable —
    // guessing here would just pick a different duplicate each session.
    if ( byCrmId && byCrmId.length > 0 ) {
      return byCrmId[0];
    }
  }

  if ( !email || !firstName || !lastName ) {
    return null;
  }

  const byIdentity = await fetchResidentsFromDrupal(
    email,
    firstName,
    lastName,
    null,
    communityNid,
  );

  // Same reasoning for taking the first: one email, one first name and one last
  // name in one community is one person, so several rows are duplicates of them.
  if ( byIdentity && byIdentity.length > 0 ) {
    return byIdentity[0];
  }

  return null;
}

/**
 * Fetches data from third party CRMS via RESTAPI interface in Drupal for Residents that match
 * any of the params.
 *
 * @param {string} email
 *   User email.
 * @param {string} firstName
 *   User first name.
 * @param {string} lastName
 *   User last name.
 * @param {string} urlSlug
 *   Url slug for the community.
 *
 * @returns {Array}
 *   List of users that match search criteria.
 */
export async function fetchResidentsFromCrmViaDrupal(
  email,
  firstName,
  lastName,
  urlSlug,
) {
  // return new Promise( async ( resolve, reject ) => {
  const queries = [];
  // Build queries based on filled out fields from the form.
  if ( email ) queries.push( `Email=${email}` );
  if ( firstName ) queries.push( `FirstName=${firstName}` );
  if ( lastName ) queries.push( `LastName=${lastName}` );

  let queryString = queries.join( '&' );
  if ( queryString ) {
    queryString = `?${queryString}`;
  }
  const url = `/third_party_apis/matching_prospects/${urlSlug}${queryString}`;

  const residents = await auth.fetchWithAuthentication( url, 'GET', null );

  return residents;
}

/**
 * Fetches data from Drupal for Residents that match any of the params.
 *
 * @param {string} email
 *  User email.
 * @param {string} firstName
 *  User first name.
 * @param {string} lastName
 *  User last name.
 * @param {string} urlSlug
 *  Url slug for the community.
 * @param {string} communityNid
 *  Community Drupal node id.
 *
 * @returns {Array}
 *   List of users that match search criteria.
 */
export async function fetchResidentsFromCrm(
  machineName,
  email,
  firstName,
  lastName,
  urlSlug,
  communityNid,
) {
  let residents = null;
  if ( machineName === 'drupal' ) {
    residents = await fetchResidentsFromDrupal(
      email,
      firstName,
      lastName,
      null,
      communityNid,
    );
  } else {
    residents = await fetchResidentsFromCrmViaDrupal(
      email,
      firstName,
      lastName,
      urlSlug,
    );
  }

  return residents;
}

/**
 * Fetches the outcome of the CRM pushes Drupal made for these residents.
 *
 * Drupal pushes to the CRM inside the JSON:API save, which has nowhere to
 * report a failure, so the result is stashed per resident and read back here.
 * Reading clears it — this is a one-shot notification, not a status field.
 *
 * @param {string} urlSlug
 *   Url slug for the community.
 * @param {Array} uuids
 *   Drupal uuids of the residents in this session.
 *
 * @returns {Object|null}
 *   { crm, pushed, items: [{ resident_name, ok, message, ... }] }, or null.
 */
export async function fetchCrmPushStatus( urlSlug, uuids ) {
  const residentUuids = ( uuids || [] ).filter( Boolean );
  if ( !urlSlug || residentUuids.length === 0 ) {
    return null;
  }

  const url = `/third_party_apis/crm_push_status/${urlSlug}?uuids=${residentUuids.join( ',' )}`;
  const status = await auth.fetchWithAuthentication( url, 'GET', null );

  // The resource answers with an error object when the user can't see this
  // community. Nothing useful to tell the counselor in that case.
  if ( !status || status.error || !Array.isArray( status.items ) ) {
    return null;
  }

  return status;
}

/**
 * Patch resident in Drupal.
 *
 * '' for spouseUuid removes the relationshing to the current spouse.
 * null for spouseUuid is leaves the relationship as is.
 *
 * @param {String} id
 *    Drupal UUID.
 * @param {Object} attributesData
 *    Data structured as an attributes node for Drupal JSON API patching.
 * @param {String} spouseUuid
 *    Spouse Drupal UUID.
 * @param {Object} relationshipsData
 *    Data structured as an relationships node for Drupal JSON API patching.
 * @returns {Object}
 */
export async function patchResidentInDrupal(
  id,
  attributesData,
  spouseUuid,
  relationshipsData,
) {
  if ( !id || !attributesData ) {
    // eslint-disable-next-line no-console
    console.error( 'Cannot patch resident. Missing id or attributesData.' );
    return {};
  }

  const data = {
    data: {
      type: 'node--resident',
      id,
    },
  };

  if ( Object.values( attributesData ).length > 0 ) {
    data.data.attributes = attributesData;
  }

  if ( spouseUuid && spouseUuid !== '' ) {
    const spouseRelationship = {
      field_spouse: {
        data: {
          type: 'node--resident',
          id: spouseUuid,
        },
      },
    };
    Object.assign( relationshipsData, spouseRelationship );
  } else if ( spouseUuid === '' ) {
    const spouseRelationship = {
      field_spouse: null,
    };
    Object.assign( relationshipsData, spouseRelationship );
  }

  if ( Object.values( relationshipsData ).length > 0 ) {
    data.data.relationships = relationshipsData;
  }

  const body = JSON.stringify( data );
  const updatedPerson = await auth.fetchWithAuthentication(
    `/jsonapi/node/resident/${id}`,
    'PATCH',
    body,
  );

  return updatedPerson;
}

/**
 * Form submit handler that is fired as the user goes between the form steps.
 *
 * @param {Object} attributesData
 * @param {String} communityUuid
 * @param {String} salespersonId
 *
 * @returns {Object}
 */
export async function postResidentInDrupal(
  attributesData,
  communityUuid,
  salespersonId,
) {
  if ( !attributesData || !communityUuid ) {
    // eslint-disable-next-line no-console
    console.error(
      'Cannot post resident. Missing attributesData or communityUuid.',
    );
    return {};
  }
  const data = {
    data: {
      type: 'node--resident',
      relationships: {
        og_audience: {
          data: {
            type: 'node--community',
            id: communityUuid,
          },
        },
        field_sales_counselor: {
          data: {
            type: 'user--user',
            id: salespersonId,
          },
        },
      },
    },
  };

  // Deliberately no field_spouse here. The CRMs that post a couple in one body
  // (Enquire, Sherpa, Lansdale) create it on the save that links the two people,
  // and whoever's save that is becomes the primary — the Enquire Prospect who
  // owns the Case. Linking at create would make person 2 that person and file
  // person 1 as their contact. Person 1's following patch does the linking.
  if ( attributesData ) {
    data.data.attributes = attributesData;
  }

  const body = JSON.stringify( data );
  const resident = await auth.fetchWithAuthentication(
    '/jsonapi/node/resident',
    'POST',
    body,
  );

  return resident;
}

export function createResidentTitle( firstName, lastName, email ) {
  let title = 'YourTour Prospect';
  if ( firstName && firstName !== '' && lastName && lastName !== '' ) {
    title = `${firstName} ${lastName}`;
  } else if ( firstName && firstName !== '' ) {
    title = firstName;
  } else if ( lastName && lastName !== '' ) {
    title = lastName;
  } else if ( email && email !== '' ) {
    title = email;
  }
  return title;
}

/**
  * Get only the residnet's YourTour sessions times.
  *
  * @param {String} residentUuid
  *    Drupal UUID.
  *
  * @returns {Array}
  */
export async function getResidentYtSessions( residentUuid ) {
  const url = `/jsonapi/node/resident/${residentUuid}/?fields[node--resident]=field_yt_sessions`;
  const data = await auth.fetchWithAuthentication( url, 'GET', null );
  const sessions = data?.data?.attributes?.field_yt_sessions || [];

  return sessions;
}
